/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button, Dropdown, message, Select, Spin } from "antd";
import {
  Search,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X as CloseIcon,
  Plus,
} from "lucide-react";

import { useDebounce } from "../../common/debounce";
import { getPlatformUserList } from "../../service/tenantService";
import { AN_COLORS, AN_FONT } from "../../design-system/tokens";
import {
  SettingsCard,
  SettingsPage,
  SettingsSection,
  SettingsStatus,
  SETTINGS_TOKENS,
} from "../../components/settings/settingsUi";
import UserEnrollment from "../../components/enrollment/UserEnrollment";
import AddUser from "../../components/directory/users/AddUser";
import ImportUsers from "../../components/directory/users/ImportUsers";

const PAGE_SIZE = 10;

// --- Local presentational helpers ---

const MONO_PALETTE = [
  { bg: "var(--an-primaryL)", fg: "var(--an-primary)" },
  { bg: "var(--an-infoBg)", fg: "var(--an-infoFg)" },
  { bg: "#E6F5EC", fg: "var(--an-success)" },
  { bg: "#FEF2D8", fg: "#A67017" },
  { bg: "#FFE7D8", fg: "#C25520" },
  { bg: "#F1E7FB", fg: "#7A3BCE" },
  { bg: "#FDE7EE", fg: "#C63362" },
  { bg: "#E0F3F5", fg: "#117E86" },
];

const monoFor = (email) => {
  if (!email) return MONO_PALETTE[0];
  let h = 0;
  for (let i = 0; i < email.length; i++) h = (h * 31 + email.charCodeAt(i)) >>> 0;
  return MONO_PALETTE[h % MONO_PALETTE.length];
};

const initialsFor = (email) => {
  if (!email) return "—";
  const name = email.split("@")[0];
  const parts = name.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const Avatar = ({ email, size = 42 }) => {
  const m = monoFor(email);
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: m.bg,
        color: m.fg,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: Math.round(size * 0.38),
        fontWeight: 600,
        fontFamily: AN_FONT,
        flexShrink: 0,
      }}
    >
      {initialsFor(email)}
    </span>
  );
};

const STATUS_TONES = {
  active:   { bg: "var(--an-successBg)", fg: "var(--an-success)", bd: "var(--an-successBd)", dot: "var(--an-success)" },
  pending:  { bg: "var(--an-warnBg)", fg: "var(--an-warn)", bd: "var(--an-warnBd)", dot: "#D99014" },
  inactive: { bg: "var(--an-dangerBg)", fg: "var(--an-danger)", bd: "var(--an-dangerBd)", dot: "var(--an-danger)" },
};

// Account status in the Settings language: a dot + text, never a coloured pill.
const STATUS_SETTINGS_TONE = { active: "success", pending: "warn", inactive: "danger" };

const StatusPill = ({ status }) => {
  const key = (status || "").toLowerCase();
  const label = status
    ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
    : "—";
  return <SettingsStatus tone={STATUS_SETTINGS_TONE[key] || "neutral"}>{label}</SettingsStatus>;
};

// Admins get the accent tone; end users stay neutral so a long list reads calmly.
const RolePill = ({ role }) => {
  const isAdmin = (role || "").toUpperCase() === "ADMIN";
  const label = !role
    ? "—"
    : role.toUpperCase() === "ENDUSER"
    ? "End user"
    : role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 9px",
        borderRadius: 99,
        background: isAdmin ? "color-mix(in srgb, #8B5CF6 14%, transparent)" : AN_COLORS.bg,
        color: isAdmin ? "color-mix(in srgb, #7A3BCE 70%, var(--an-ink))" : AN_COLORS.ink2,
        border: `1px solid ${isAdmin ? "color-mix(in srgb, #8B5CF6 30%, transparent)" : AN_COLORS.line}`,
        fontSize: 11.5,
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
};

const MfaPills = ({ factors }) => {
  if (!factors || factors.length === 0) {
    return <span style={{ color: AN_COLORS.muted, fontSize: 12.5 }}>Not enrolled</span>;
  }
  return (
    <span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>
      {factors.map((f) => (
        <span
          key={f}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "2px 9px 2px 7px",
            borderRadius: 99,
            background: STATUS_TONES.active.bg,
            color: STATUS_TONES.active.fg,
            border: `1px solid ${STATUS_TONES.active.bd}`,
            fontSize: 11.5,
            fontWeight: 500,
            whiteSpace: "nowrap",
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: STATUS_TONES.active.dot,
            }}
          />
          {f}
        </span>
      ))}
    </span>
  );
};

const smallBtn = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 12px",
  background: "var(--an-surface)",
  border: `1px solid ${AN_COLORS.line}`,
  borderRadius: 6,
  fontSize: 12,
  color: AN_COLORS.ink2,
  fontFamily: AN_FONT,
};

const EdStat = ({ label, value, hint, color }) => {
  const display =
    value == null ? "—" : typeof value === "number" ? value.toLocaleString() : value;
  return (
    <div style={{ padding: SETTINGS_TOKENS.rowPad }}>
      <div style={{ fontSize: 12.5, fontWeight: 500, color: AN_COLORS.muted, marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <div
          style={{
            fontSize: 24,
            fontWeight: 600,
            letterSpacing: -0.5,
            lineHeight: 1,
            color: color || AN_COLORS.ink,
          }}
        >
          {display}
        </div>
        {hint && <div style={{ fontSize: 12, color: AN_COLORS.muted }}>{hint}</div>}
      </div>
    </div>
  );
};

const CELL_LABEL = {
  fontSize: 11.5,
  fontWeight: 500,
  color: AN_COLORS.muted,
  marginBottom: 4,
};

// Status values map 1:1 to the API's `status` field.
const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "pending", label: "Pending" },
];

const ROLE_PAYLOAD = {
  all: ["ADMIN", "ENDUSER"],
  ADMIN: ["ADMIN"],
  ENDUSER: ["ENDUSER"],
};

const SORT_KEYS = {
  name: "fullName",
  email: "emailId",
  role: "role",
  status: "status",
};

/**
 * The tenant's platform users — server-side paginated, with the status
 * breakdown coming straight from the response's `counts` object.
 *
 * Self-contained (page header, stats, toolbar, rows, pagination) in the
 * Settings design language (settingsUi), so host pages only need to render
 * <PlatformUsers title=… description=… />. Pass showStats={false} when the host
 * already owns a stats row.
 */
export default function PlatformUsers({
  showStats = true,
  title = "Users & Access",
  description = "People with an account on this tenant. Review their role, enrolled MFA factors, and account status.",
}) {
  const location = useLocation();
  const [notification, notificationContext] = message.useMessage();

  // Cross-page handoffs (e.g. Endpoint User Details) arrive with an email to
  // pre-filter on.
  const [search, setSearch] = useState(location?.state?.email_address || "");
  const debouncedSearch = useDebounce(search, 600);
  const [statusFilter, setStatusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [sortBy, setSortBy] = useState("default");
  const [page, setPage] = useState(1);

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({
    total: null,
    active: null,
    inactive: null,
    pending: null,
  });
  const [isLoading, setIsLoading] = useState(false);

  const [isAddUser, setIsAddUser] = useState(false);
  const [isImportUser, setIsImportUser] = useState(false);

  // Row whose enrollment detail is open — one at a time, as on the AD list.
  const [enrollmentUserId, setEnrollmentUserId] = useState(null);

  // Any filter change invalidates the current page offset.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, roleFilter]);

  const fetchPlatformUsers = () => {
    setIsLoading(true);
    getPlatformUserList({
      payload: {
        roles: ROLE_PAYLOAD[roleFilter] || ROLE_PAYLOAD.all,
        status: statusFilter,
        search: debouncedSearch || "",
        page,
        pageSize: PAGE_SIZE,
      },
      onSuccess: (res) => {
        setIsLoading(false);
        setRows(
          (res?.data || []).map((u) => ({
            id: u.userId,
            // Named emailId so the Avatar/initials helpers work unchanged.
            emailId: u.email,
            fullName: [u.firstName, u.lastName].filter(Boolean).join(" ").trim(),
            role: u.role,
            roleId: u.roleId,
            status: u.status,
            mfaEnrolled: Array.isArray(u.mfaEnrolled) ? u.mfaEnrolled : [],
            firstLogin: u.firstLogin,
            tenantId: u.tenantId,
          }))
        );
        setCounts(res?.counts || {});
      },
      onFailure: () => {
        setIsLoading(false);
        notification.error({ content: "Unable to fetch platform users" });
      },
    });
  };

  useEffect(() => {
    fetchPlatformUsers();
  }, [debouncedSearch, statusFilter, roleFilter, page]);

  const addUsersMenu = useMemo(
    () => ({
      // onClick on the item (not the label) so the whole row is clickable.
      items: [
        { key: "add", label: "Add User", onClick: () => setIsAddUser(true) },
        {
          key: "import",
          label: "Import Users",
          onClick: () => setIsImportUser(true),
        },
      ],
    }),
    []
  );

  // Sort applies to the current page only, matching the AD Users screen.
  const sortedRows = useMemo(() => {
    const k = SORT_KEYS[sortBy];
    if (!k) return rows;
    return [...rows].sort((a, b) =>
      String(a[k] || "").localeCompare(String(b[k] || ""), undefined, {
        numeric: true,
        sensitivity: "base",
      })
    );
  }, [rows, sortBy]);

  // `counts` is scoped to the current search + roles but not to status, so a
  // specific status tab reads its own bucket. Falls back to the page length if
  // the backend scopes counts differently.
  const displayTotal =
    statusFilter === "all"
      ? counts.total ?? rows.length
      : counts[statusFilter] ?? rows.length;

  const from = displayTotal === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, displayTotal);
  const hasPrev = page > 1;
  const hasNext = to < displayTotal;

  return (
    <>
      {notificationContext}
      <SettingsPage
        title={title}
        description={description}
        actions={
          <Dropdown menu={addUsersMenu} placement="bottomRight">
            <Button type="primary" icon={<Plus size={14} style={{ verticalAlign: -2 }} />}>
              Add users
            </Button>
          </Dropdown>
        }
      >
          {showStats && (
            <SettingsCard>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              <EdStat label="Total" value={counts.total} />
              <EdStat
                label="Active"
                value={counts.active}
                color="var(--an-success)"
                hint={
                  counts.total > 0 && counts.active != null
                    ? `${Math.round((counts.active / counts.total) * 1000) / 10}%`
                    : null
                }
              />
              <EdStat label="Inactive" value={counts.inactive} color="var(--an-danger)" />
              <EdStat label="Pending" value={counts.pending} color="#D99014" />
            </div>
            </SettingsCard>
          )}

      <SettingsSection
        title="People"
        description="Search, filter, and open a person to manage their authenticator enrollment."
        bare
      >
      <Spin spinning={isLoading}>
        <div style={{ fontFamily: AN_FONT, color: AN_COLORS.ink, display: "grid", gap: 12 }}>

          {/* Toolbar */}
          <style>{`
            .an-endpoint-select.ant-select {
              width: 200px !important;
              height: 36px !important;
              line-height: 36px !important;
              flex-shrink: 0 !important;
            }
            .an-endpoint-select .ant-select-selector {
              border-radius: 99px !important;
              height: 36px !important;
              padding: 0 14px !important;
              border: 1px solid ${AN_COLORS.line} !important;
              box-shadow: none !important;
              font-family: ${AN_FONT};
              font-size: 13px !important;
              display: flex;
              align-items: center;
            }
            .an-endpoint-select .ant-select-selection-search {
              inset-inline-start: 14px !important;
              inset-inline-end: 14px !important;
            }
            .an-endpoint-select .ant-select-selection-search-input {
              height: 34px !important;
            }
            .an-endpoint-select .ant-select-selection-placeholder,
            .an-endpoint-select .ant-select-selection-item {
              line-height: 34px !important;
              color: ${AN_COLORS.ink2};
            }
            .an-endpoint-select .ant-select-selection-placeholder {
              color: ${AN_COLORS.muted};
            }
            .an-endpoint-select.ant-select-focused .ant-select-selector,
            .an-endpoint-select:hover .ant-select-selector {
              border-color: ${AN_COLORS.ink3} !important;
            }
          `}</style>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            {/* Search */}
            <div style={{ position: "relative", width: 320 }}>
              <span
                style={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: AN_COLORS.muted,
                  display: "inline-flex",
                }}
              >
                <Search size={14} />
              </span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name or email"
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: search ? "9px 34px 9px 34px" : "9px 12px 9px 34px",
                  border: `1px solid ${AN_COLORS.line}`,
                  borderRadius: 99,
                  fontSize: 13,
                  fontFamily: AN_FONT,
                  background: "var(--an-surface)",
                  outline: "none",
                }}
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                  title="Clear search"
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    border: "none",
                    background: AN_COLORS.line2,
                    color: AN_COLORS.muted,
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                  }}
                >
                  <CloseIcon size={12} />
                </button>
              )}
            </div>

            {/* Status segmented control */}
            <div
              style={{
                display: "flex",
                gap: 2,
                background: "var(--an-surface)",
                padding: 3,
                border: `1px solid ${AN_COLORS.line}`,
                borderRadius: 99,
              }}
            >
              {STATUS_OPTIONS.map((opt) => {
                const isActive = statusFilter === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setStatusFilter(opt.value)}
                    style={{
                      padding: "5px 12px",
                      fontSize: 12,
                      fontFamily: AN_FONT,
                      border: "none",
                      borderRadius: 99,
                      cursor: "pointer",
                      background: isActive ? AN_COLORS.ink : "transparent",
                      color: isActive ? AN_COLORS.surface : AN_COLORS.ink2,
                      fontWeight: isActive ? 500 : 400,
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            <Select
              value={roleFilter}
              onChange={(v) => setRoleFilter(v)}
              className="an-endpoint-select"
              style={{ width: 200, flexShrink: 0 }}
              aria-label="Filter by role"
              options={[
                { value: "all", label: "Role: All" },
                { value: "ADMIN", label: "Role: Admin" },
                { value: "ENDUSER", label: "Role: End user" },
              ]}
            />

            <Select
              value={sortBy}
              onChange={(v) => setSortBy(v)}
              className="an-endpoint-select"
              style={{ width: 200, flexShrink: 0 }}
              aria-label="Sort by"
              options={[
                { value: "default", label: "Sort: Default" },
                { value: "name", label: "Sort: Name" },
                { value: "email", label: "Sort: Email" },
                { value: "role", label: "Sort: Role" },
                { value: "status", label: "Sort: Status" },
              ]}
            />
          </div>

          {/* Row-cards */}
          {sortedRows.length === 0 && !isLoading ? (
            <SettingsCard>
              <div
                style={{
                  padding: "40px 20px",
                  textAlign: "center",
                  color: AN_COLORS.muted,
                  fontSize: 13,
                }}
              >
                No platform users found.
              </div>
            </SettingsCard>
          ) : (
            <SettingsCard>
              {sortedRows.map((user, index) => {
                const enrollmentOpen = enrollmentUserId === user.id;
                return (
                <div
                  key={user.id}
                  style={{
                    padding: SETTINGS_TOKENS.rowPad,
                    borderBottom:
                      index === sortedRows.length - 1
                        ? "none"
                        : `1px solid ${SETTINGS_TOKENS.hairline}`,
                  }}
                >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "36px 1.7fr 130px 1fr 110px 28px",
                    gap: 16,
                    alignItems: "center",
                  }}
                >
                  <Avatar email={user.emailId} size={36} />

                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 14.5,
                        fontWeight: 600,
                        color: AN_COLORS.ink,
                        letterSpacing: -0.1,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        textTransform: user.fullName ? "capitalize" : "none",
                      }}
                      title={user.fullName || user.emailId}
                    >
                      {user.fullName || user.emailId || "—"}
                    </div>
                    {user.fullName && (
                      <div
                        style={{
                          fontSize: 12,
                          color: AN_COLORS.muted,
                          marginTop: 3,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={user.emailId}
                      >
                        {user.emailId}
                      </div>
                    )}
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <div style={CELL_LABEL}>Role</div>
                    <RolePill role={user.role} />
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <div style={CELL_LABEL}>MFA enrolled</div>
                    <MfaPills factors={user.mfaEnrolled} />
                  </div>

                  <StatusPill status={user.status} />

                  <button
                    type="button"
                    onClick={() => setEnrollmentUserId(enrollmentOpen ? null : user.id)}
                    aria-expanded={enrollmentOpen}
                    aria-label={
                      enrollmentOpen
                        ? "Hide authenticator enrollment"
                        : "Show authenticator enrollment"
                    }
                    title="Authenticator enrollment"
                    style={{
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      padding: 0,
                      width: 28,
                      height: 28,
                      borderRadius: 6,
                      color: AN_COLORS.muted,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {enrollmentOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                  </button>
                </div>

                {enrollmentOpen && (
                  <div
                    style={{
                      marginTop: 14,
                      paddingTop: 14,
                      borderTop: `1px solid ${SETTINGS_TOKENS.hairline}`,
                    }}
                  >
                    {/* `id` on these rows is the platform userId (see the row
                        mapping in fetchUsers). No `domain` is passed: console
                        users are not AD logons, so the vendor-MFA guard that
                        applies to a directory does not apply here. */}
                    <UserEnrollment
                      userType="console"
                      userId={user.id}
                      email={user.emailId}
                      onNotify={({ type, content }) => notification[type]?.({ content })}
                    />
                  </div>
                )}
                </div>
                );
              })}
            </SettingsCard>
          )}

          {/* Pagination */}
          {displayTotal > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginTop: 4,
                fontSize: 12.5,
                color: AN_COLORS.muted,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              <span>
                Showing {from}–{to} of {displayTotal}
              </span>
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  disabled={!hasPrev}
                  onClick={() => setPage((p) => p - 1)}
                  style={{
                    ...smallBtn,
                    opacity: hasPrev ? 1 : 0.4,
                    cursor: hasPrev ? "pointer" : "not-allowed",
                  }}
                >
                  <ChevronLeft size={12} /> Previous
                </button>
                <button
                  disabled={!hasNext}
                  onClick={() => setPage((p) => p + 1)}
                  style={{
                    ...smallBtn,
                    opacity: hasNext ? 1 : 0.4,
                    cursor: hasNext ? "pointer" : "not-allowed",
                  }}
                >
                  Next <ChevronRight size={12} />
                </button>
              </div>
            </div>
          )}
        </div>
      </Spin>
      </SettingsSection>
      </SettingsPage>

      {isAddUser && (
        <AddUser
          isAddUser={isAddUser}
          setIsAddUser={setIsAddUser}
          notification={notification}
          fetchUsers={fetchPlatformUsers}
          run={false}
          setRun={() => {}}
        />
      )}
      {isImportUser && (
        <ImportUsers
          isImportUser={isImportUser}
          setIsImportUser={setIsImportUser}
          notification={notification}
          fetchUsers={fetchPlatformUsers}
          run={false}
          setRun={() => {}}
        />
      )}
    </>
  );
}
